import styles from './StatusBadge.module.css';

export type DomainVerificationStatus = 'unverified' | 'pending' | 'verified' | string;

/** Which status vocabulary a badge renders. Defaults to domain verification. */
export type StatusBadgeKind = 'domain' | 'scan' | 'journey' | 'execution' | 'integration' | 'severity' | 'capability' | 'source' | 'connected-validation';

const LABELS: Record<string, string> = {
  unverified: 'Unverified',
  pending: 'Verification pending',
  verified: 'Verified',
};

const SCAN_LABELS: Record<string, string> = {
  initiated: 'Initiated',
  running: 'Running',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

const JOURNEY_LABELS: Record<string, string> = {
  discovered: 'Discovered',
  configuration_required: 'Configuration required',
};

// Execution-derived outcome of a journey type's latest terminal run (#40b). The
// eight values mirror the backend RunClassifiableOutcome set.
const EXECUTION_LABELS: Record<string, string> = {
  validated: 'Validated',
  partially_validated: 'Partially validated',
  failed: 'Failed',
  flaky: 'Flaky',
  blocked: 'Blocked',
  error: 'Error',
  cancelled: 'Cancelled',
  configuration_required: 'Configuration required',
};

// Connection state of a third-party integration (#47, FR-10.3). `coming-soon` is
// hyphenated to match the API state value.
const INTEGRATION_LABELS: Record<string, string> = {
  connected: 'Connected',
  disconnected: 'Disconnected',
  'coming-soon': 'Coming soon',
};

function toneClass(status: string): string | undefined {
  if (status === 'unverified') return styles.warning;
  if (status === 'verified') return styles.success;
  return styles.info;
}

function scanToneClass(status: string): string | undefined {
  if (status === 'running') return styles.accent;
  if (status === 'completed') return styles.success;
  if (status === 'failed') return styles.danger;
  if (status === 'cancelled') return styles.neutral;
  return styles.info; // initiated (and any unknown scan state)
}

function journeyToneClass(status: string): string | undefined {
  // Attention (warning) for "needs configuration", never danger — a journey is
  // only discovered here, never failed/validated (#40b). `discovered` is neutral.
  if (status === 'configuration_required') return styles.warning;
  return styles.neutral;
}

function executionToneClass(status: string): string | undefined {
  // Success = a real validated run; danger = a real failure. Warning = attention
  // (needs config / partial / flaky). Everything else (blocked/error/cancelled)
  // is neutral — informational, not a pass or a fail. ZERO new tokens.
  if (status === 'validated') return styles.success;
  if (status === 'failed') return styles.danger;
  if (status === 'flaky' || status === 'partially_validated' || status === 'configuration_required')
    return styles.warning;
  return styles.neutral;
}

function integrationToneClass(status: string): string | undefined {
  // Success = a live connection; info = an upcoming/coming-soon integration;
  // neutral = disconnected (and any unknown state). ZERO new tokens.
  if (status === 'connected') return styles.success;
  if (status === 'coming-soon') return styles.info;
  return styles.neutral;
}

// Product-defect severity (#37/37b). Four labeled tiers over three token tones —
// text distinguishes Critical vs High (WCAG 1.4.1). ZERO new tokens.
const SEVERITY_LABELS: Record<string, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

function severityToneClass(status: string): string | undefined {
  if (status === 'critical' || status === 'high') return styles.danger;
  if (status === 'medium') return styles.warning;
  return styles.neutral; // low (and any unknown severity)
}

// BYOK model capability verdict (#31-B). verified=success, limited=warning,
// inconclusive=neutral (a verdict, not a failure), failed=danger (infra error).
// Text distinguishes each (WCAG 1.4.1). ZERO new tokens.
const CAPABILITY_LABELS: Record<string, string> = {
  verified: 'Verified',
  limited: 'Limited',
  inconclusive: 'Inconclusive',
  failed: 'Could not complete',
};

function capabilityToneClass(status: string): string | undefined {
  if (status === 'verified') return styles.success;
  if (status === 'limited') return styles.warning;
  if (status === 'failed') return styles.danger;
  return styles.neutral; // inconclusive (and any unknown)
}

// Provenance of a journey (#133/#243). customer-provided = accent, discovered =
// neutral; the text label distinguishes them (WCAG 1.4.1). ZERO new tokens.
const SOURCE_LABELS: Record<string, string> = {
  'customer-provided': 'Customer provided',
  discovered: 'Discovered',
};

function sourceToneClass(status: string): string | undefined {
  if (status === 'customer-provided') return styles.accent;
  return styles.neutral; // discovered (and any unknown)
}

// Connected Validation readiness rows (#430). A small closed vocabulary covering
// the authorization, credentials, and runnable tri-states. Each value carries its
// own TEXT label (WCAG 1.4.1); the held-reason variants ("Add test credentials" /
// "Enable Connected Validation for this boundary") are their own statuses so the
// badge is self-describing. ZERO new tokens.
const CONNECTED_VALIDATION_LABELS: Record<string, string> = {
  authorized: 'Authorized',
  'not-authorized': 'Not authorized',
  'credentials-saved': 'Credentials saved',
  'not-saved': 'Not saved',
  runnable: 'Runnable',
  'add-credentials': 'Add test credentials',
  'enable-boundary': 'Enable Connected Validation for this boundary',
};

function connectedValidationToneClass(status: string): string | undefined {
  // Success = a satisfied gate; warning = an actionable held reason; neutral = a
  // not-yet-satisfied prerequisite. ZERO new tokens.
  if (status === 'authorized' || status === 'credentials-saved' || status === 'runnable')
    return styles.success;
  if (status === 'add-credentials' || status === 'enable-boundary') return styles.warning;
  return styles.neutral; // not-authorized / not-saved (and any unknown)
}

export interface StatusBadgeProps {
  status: DomainVerificationStatus;
  /** The status vocabulary to render. Defaults to `'domain'` so existing
   * domain-verification usages are unchanged. */
  kind?: StatusBadgeKind;
}

/**
 * Status pill for domain-verification or scan states. The status is conveyed by
 * a visible TEXT label (not colour alone) plus a `title`, so it is accessible to
 * screen-reader and colour-blind users (WCAG 1.4.1).
 */
export function StatusBadge({ status, kind = 'domain' }: StatusBadgeProps) {
  const isScan = kind === 'scan';
  const isJourney = kind === 'journey';
  const isExecution = kind === 'execution';
  const isIntegration = kind === 'integration';
  const isSeverity = kind === 'severity';
  const isCapability = kind === 'capability';
  const isSource = kind === 'source';
  const isConnectedValidation = kind === 'connected-validation';
  const label =
    (isConnectedValidation
      ? CONNECTED_VALIDATION_LABELS[status]
      : isSource
        ? SOURCE_LABELS[status]
        : isCapability
          ? CAPABILITY_LABELS[status]
          : isSeverity
            ? SEVERITY_LABELS[status]
            : isIntegration
              ? INTEGRATION_LABELS[status]
              : isExecution
                ? EXECUTION_LABELS[status]
                : isJourney
                  ? JOURNEY_LABELS[status]
                  : isScan
                    ? SCAN_LABELS[status]
                    : LABELS[status]) ?? status;
  const tone = isConnectedValidation
    ? connectedValidationToneClass(status)
    : isSource
      ? sourceToneClass(status)
      : isCapability
        ? capabilityToneClass(status)
        : isSeverity
          ? severityToneClass(status)
          : isIntegration
            ? integrationToneClass(status)
            : isExecution
              ? executionToneClass(status)
              : isJourney
                ? journeyToneClass(status)
                : isScan
                  ? scanToneClass(status)
                  : toneClass(status);
  const classes = [styles.badge, tone].filter(Boolean).join(' ');
  const prefix = isConnectedValidation
    ? 'Connected Validation'
    : isSource
      ? 'Source'
      : isCapability
        ? 'Capability'
        : isSeverity
          ? 'Severity'
          : isIntegration
            ? 'Integration'
            : isExecution
              ? 'Latest run'
              : isJourney
                ? 'Journey'
                : isScan
                  ? 'Scan'
                  : 'Domain';
  const title = `${prefix} status: ${label}`;
  return (
    <span className={classes} title={title}>
      {label}
    </span>
  );
}
